import { useMemo, useState } from 'react';
import { useOrganizations } from '@/domains/accounts/services/accounts';
import { useAddTerminalToOrganization } from '@/domains/terminals/services/user-terminals';
import { UserTerminal } from '@/shared/types/userTerminalTypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Loader2, Search, User } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import EmptyView from '@/shared/components/EmptyView';
import SearchBar from '@/shared/components/SearchBar';

interface AddTerminalsToAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  terminal: UserTerminal;
  selectedRegionCode: string;
}

export default function AddTerminalsToAccountDialog({
  open,
  onOpenChange,
  onSuccess,
  terminal,
  selectedRegionCode,
}: AddTerminalsToAccountDialogProps) {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState("");
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizations(open, selectedRegionCode);
  const addTerminalToOrganization = useAddTerminalToOrganization();

  // Filter out the organization that already has this terminal
  const availableOrganizations = useMemo(() => {
    return organizations?.filter(organization =>
      organization.organizationId !== terminal.organization?.organizationId
    ) || [];
  }, [organizations, terminal.organization?.organizationId]);

  // Filter organizations based on search term
  const filteredOrganizations = useMemo(() => {
    return availableOrganizations.filter(organization =>
      organization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      organization.organizationId.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
  }, [availableOrganizations, searchTerm]);

  const handleSubmit = async () => {
    if (!selectedOrganizationId) {
      showError(ERROR.TERMINAL.SELECT_ACCOUNT);
      return;
    }

    try {
      await addTerminalToOrganization.mutateAsync({
        organizationId: selectedOrganizationId,
        terminalId: terminal.userTerminalId
      });

      showSuccess(SUCCESS.TERMINAL.ADDED_TO_ACCOUNT);
      onSuccess();
      onOpenChange(false);
      setSelectedOrganizationId('');
      setSearchTerm("");
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.ADD_TO_ACCOUNT);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setSearchTerm("");
      setSelectedOrganizationId('');
    }
    onOpenChange(open);
  };

  const showSearch = availableOrganizations.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Terminal to Organization</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4">
            <h4 className="font-medium mb-2">Terminal Details</h4>
            <p className="text-sm">
              Terminal ID: {terminal.userTerminalId}
              {terminal.kitSerialNumber && ` | Kit: ${terminal.kitSerialNumber}`}
              {terminal.dishSerialNumber && ` | Dish: ${terminal.dishSerialNumber}`}
            </p>
          </div>

          <div className="space-y-4">
            {/* Search Input - only show when there are accounts */}
            {showSearch && (
              <div className="relative">
                <SearchBar value={searchTerm} onChange={(value) => setSearchTerm(value)} placeholder="Search accounts..." />
              </div>
            )}

            <h4 className="font-medium">Select Account</h4>
            {isLoadingOrganizations ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : filteredOrganizations.length === 0 ? (
              searchTerm ? (
                <EmptyView
                  icon={Search}
                  size="small"
                  title="No Accounts Found"
                  description={`No accounts match "${searchTerm}". Try adjusting your search.`}
                />
              ) : (
                <EmptyView
                  icon={User}
                  size="small"
                  title={terminal.organization
                    ? "All Organizations Already Have This Terminal"
                    : "No Organizations Available"}
                  description={terminal.organization
                    ? "This terminal is already associated with all available organizations."
                    : "There are no organizations available to add this terminal to."}
                />
              )
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                <RadioGroup
                  value={selectedOrganizationId}
                  onValueChange={setSelectedOrganizationId}
                  className="space-y-2"
                >
                  {filteredOrganizations.map((organization) => (
                    <div key={organization.organizationId} className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={organization.organizationId}
                        id={`account-${organization.organizationId}`}
                      />
                      <Label
                        htmlFor={`account-${organization.organizationId}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        <div className="flex flex-col">
                          <span>{organization.name}</span>
                          <span className="text-xs">ID: {organization.organizationId}</span>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => handleDialogClose(false)}
            disabled={addTerminalToOrganization.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={addTerminalToOrganization.isPending || !selectedOrganizationId || availableOrganizations.length === 0}
            className="gap-2">
            {addTerminalToOrganization.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              'Add to Selected Account'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 